import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { MessageSquare, Mail, User, Trash2, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { tenantAPI } from '../../utils/api';
import { logger } from '../../utils/logger';

const OfflineMessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState(null); // null = all, true = read, false = unread
  const [expandedId, setExpandedId] = useState(null);

  const tenant = JSON.parse(localStorage.getItem('tenant') || '{}');

  const fetchMessages = useCallback(async () => {
    if (!tenant?.id) return;
    setLoading(true);
    try {
      const params = { page, page_size: 15 };
      if (filter !== null) params.is_read = filter;
      const data = await tenantAPI.getOfflineMessages(tenant.id, params);
      setMessages(data.messages || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      logger.error('Failed to fetch offline messages:', error);
    } finally {
      setLoading(false);
    }
  }, [tenant?.id, page, filter]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleExpand = async (msg) => {
    if (expandedId === msg.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(msg.id);
    if (!msg.is_read) {
      try {
        await tenantAPI.markOfflineMessageRead(tenant.id, msg.id);
        setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        logger.error('Failed to mark message as read:', error);
      }
    }
  };

  const handleArchive = async (e, messageId) => {
    e.stopPropagation();
    try {
      await tenantAPI.archiveOfflineMessage(tenant.id, messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      setTotal(prev => prev - 1);
      if (expandedId === messageId) setExpandedId(null);
    } catch (error) {
      logger.error('Failed to archive message:', error);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="h-full overflow-y-auto p-6">
      <Card className="border-2 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-600" />
                Offline Messages
                {unreadCount > 0 && (
                  <Badge className="bg-red-500 text-white ml-2">{unreadCount} unread</Badge>
                )}
              </CardTitle>
              <CardDescription>Messages left by visitors when your chatbot was offline</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchMessages} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2 mt-4">
            {[
              { value: null, label: 'All' },
              { value: false, label: 'Unread' },
              { value: true, label: 'Read' },
            ].map(f => (
              <button
                key={String(f.value)}
                onClick={() => { setFilter(f.value); setPage(1); }}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                  filter === f.value
                    ? 'bg-[var(--g6-primary)] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading messages...</span>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <MessageSquare className="w-12 h-12 mb-3" />
              <p className="text-lg font-medium">No offline messages</p>
              <p className="text-sm">Messages from visitors will appear here</p>
            </div>
          ) : (
            <div className="divide-y">
              {messages.map(msg => (
                <div key={msg.id}>
                  <div
                    onClick={() => handleExpand(msg)}
                    className={`flex items-center gap-4 px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      !msg.is_read ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <div className="w-2 flex-shrink-0">
                      {!msg.is_read && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className={`text-sm truncate ${!msg.is_read ? 'font-semibold' : 'font-medium'}`}>
                          {msg.visitor_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-500 truncate">{msg.visitor_email}</span>
                      </div>
                    </div>

                    <div className="flex-[2] min-w-0 hidden md:block">
                      <p className="text-sm text-gray-600 truncate">{msg.message}</p>
                    </div>

                    <div className="text-xs text-gray-400 flex-shrink-0 hidden sm:block">
                      {formatDate(msg.created_at)}
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={(e) => handleArchive(e, msg.id)}
                        className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                        title="Archive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {expandedId === msg.id && (
                    <div className="px-10 py-4 bg-gray-50 border-t border-gray-100">
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <span><strong>From:</strong> {msg.visitor_name} ({msg.visitor_email})</span>
                        <span><strong>Date:</strong> {formatDate(msg.created_at)}</span>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{msg.message}</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t bg-gray-50">
              <span className="text-sm text-gray-500">
                {total} message{total !== 1 ? 's' : ''} total
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OfflineMessagesPage;
